/**
 * Chip picker + Play/Clear buttons — DOM ports of the two amgcommon_v2
 * widgets used by the Flutter Dragon Tiger build:
 *
 *   ChipsSelection   → packages/amgcommon_v2/lib/widgets/chips_selection.dart
 *   PlayClearButton  → packages/amgcommon_v2/lib/widgets/play_clear_button.dart
 *
 * Visuals preserved 1:1 (CDN coin/highlight/button assets, rotating ring,
 * breathing scale, diagonal sweep, press-scale feedback).
 * Animations are CSS-driven (transform/opacity only) so they stay GPU-cheap
 * — critical given this build's perf-benchmark intent.
 *
 * Bet-area clicks are NOT handled here — those come from the betting Rive
 * via `placeBetClick` events.
 */
import type { GameConfig } from '../types/game';

const COIN_BASE = 'https://imagebank.b-cdn.net/AMG/Common/imgCoins';

export interface ControlsHandlers {
  onSelectChip: (index: number) => void;
  onClear: () => void;
  onConfirm: () => void;
}

export class TableControls {
  private readonly host: HTMLElement;
  private readonly chipBar: HTMLElement;
  private readonly pcGroup: HTMLElement;
  private readonly clearBtn: HTMLElement;
  private readonly playBtn: HTMLElement;
  private chipPicks: HTMLElement[] = [];
  private readonly handlers: ControlsHandlers;
  private currentSelectedIndex = 0;

  constructor(handlers: ControlsHandlers) {
    const host = document.getElementById('dom-hud');
    if (!host) throw new Error('TableControls: #dom-hud missing');
    this.host = host;
    this.handlers = handlers;

    // ----- Chip picker bar -----
    this.chipBar = document.createElement('div');
    this.chipBar.className = 'chip-bar';
    this.host.appendChild(this.chipBar);

    // ----- Play/Clear group (vertical stack, right-aligned) -----
    this.pcGroup = document.createElement('div');
    this.pcGroup.className = 'pc-buttons';

    // CLEAR button — image + overlaid "CLEAR" text
    // (play_clear_button.dart:163-194).
    this.clearBtn = this.makePcButton('pc-btn-clear', 'CLEAR');
    // PLAY button — image only; the CDN btnPlay.png already has "PLAY"
    // baked in (play_clear_button.dart:201-234, no Text child).
    this.playBtn = this.makePcButton('pc-btn-play', null);
    this.pcGroup.append(this.clearBtn, this.playBtn);
    this.host.appendChild(this.pcGroup);

    // Click + press-scale animation (mirrors play_clear_button.dart:71-104).
    this.bindPressFeedback(this.clearBtn, () => handlers.onClear());
    this.bindPressFeedback(this.playBtn, () => handlers.onConfirm());
  }

  private makePcButton(modifierClass: string, label: string | null): HTMLElement {
    const el = document.createElement('div');
    el.className = `pc-btn ${modifierClass}`;
    if (label != null) {
      const span = document.createElement('span');
      span.className = 'pc-btn-label';
      span.textContent = label;
      el.appendChild(span);
    }
    return el;
  }

  private bindPressFeedback(el: HTMLElement, onTap: () => void): void {
    const down = (): void => {
      if (el.classList.contains('disabled')) return;
      el.classList.add('pressed');
    };
    const up = (): void => {
      if (!el.classList.contains('pressed')) return;
      el.classList.remove('pressed');
      if (!el.classList.contains('disabled')) onTap();
    };
    const cancel = (): void => el.classList.remove('pressed');
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointerleave', cancel);
    el.addEventListener('pointercancel', cancel);
  }

  /**
   * Build the chip picker once the GameConfig is known. Mirrors the
   * `chipValues.asMap().entries.map((entry) => ...)` build in
   * chips_selection.dart:189-207. Each chip uses imgCoins{index+1}.png.
   */
  bindChips(config: GameConfig): void {
    this.chipBar.innerHTML = '';
    this.chipPicks = [];
    config.chipValues.forEach((value, i) => {
      const pick = document.createElement('div');
      pick.className = 'chip-pick';
      // Rotating highlight ring (only visible when .active).
      const ring = document.createElement('div');
      ring.className = 'chip-ring';
      // Coin sprite + breathing scale animation.
      const coin = document.createElement('div');
      coin.className = 'chip-coin';
      coin.style.backgroundImage = `url('${COIN_BASE}${(i % 6) + 1}.png')`;
      // Sweep light effect over the chip face.
      const sweep = document.createElement('div');
      sweep.className = 'chip-sweep';
      coin.appendChild(sweep);
      // Value label centered on the coin.
      const label = document.createElement('div');
      label.className = 'chip-label';
      label.textContent = formatChipLabel(value);
      coin.appendChild(label);

      pick.append(ring, coin);
      pick.addEventListener('click', () => this.handlers.onSelectChip(i));
      this.chipBar.appendChild(pick);
      this.chipPicks.push(pick);
    });
    this.setSelectedChip(this.currentSelectedIndex);
  }

  setSelectedChip(index: number): void {
    this.currentSelectedIndex = index;
    this.chipPicks.forEach((b, i) => b.classList.toggle('active', i === index));
  }

  /**
   * Enable / disable controls. Mirrors the conditional `onPlayPressed` /
   * `onClearPressed` callbacks + `IgnorePointer` wrap in game_screen.dart:914-968.
   *
   *   canBet   — currently in betting phase (chip picker can be tapped).
   *   hasBets  — at least one bet placed (play/clear active).
   */
  setControlsEnabled(canBet: boolean, hasBets: boolean): void {
    this.chipBar.classList.toggle('disabled', !canBet);
    this.clearBtn.classList.toggle('disabled', !(canBet && hasBets));
    this.playBtn.classList.toggle('disabled', !(canBet && hasBets));
  }
}

/** "1000" → "1K", "1500000" → "1.5M". Matches the visual style of the bg coins. */
function formatChipLabel(v: number): string {
  if (v >= 1_000_000) {
    const m = v / 1_000_000;
    return Number.isInteger(m) ? `${m}M` : `${m.toFixed(1)}M`;
  }
  if (v >= 1_000) {
    const k = v / 1_000;
    return Number.isInteger(k) ? `${k}K` : `${k.toFixed(1)}K`;
  }
  return String(v);
}
